'use client';

import { usePathname } from 'next/navigation';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';

interface RightDrawerContextType {
  isOpen: boolean;
  isFixed: boolean;
  content: ReactNode | null;
  title: ReactNode;
  openDrawer: (content: ReactNode, title?: ReactNode, fixed?: boolean) => void;
  closeDrawer: () => void;
  setDrawerContent: (content: ReactNode) => void;
  setDrawerTitle: (title: ReactNode) => void;
  setDrawerWidth: (w: number) => void;
  drawerWidth: number;
}

const RightDrawerContext = createContext<RightDrawerContextType | undefined>(undefined);

export const RightDrawerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<ReactNode>('');
  const [drawerWidth, setDrawerWidth] = useState<number>(400);
  const pathname = usePathname();

  const openDrawer = useCallback((content: ReactNode, title: ReactNode = '', fixed = false) => {
    setContent(content);
    setTitle(title);
    setIsOpen(true);
    setIsFixed(fixed);
  }, []);

  const closeDrawer = () => {
    setIsOpen(false);
    setContent(null);
    setTitle('');
    setDrawerWidth(400);
  };

  const setDrawerContent = (content: ReactNode) => {
    setContent(content);
  };

  const setDrawerTitle = (title: ReactNode) => {
    setTitle(title);
  };

  useEffect(() => {
    closeDrawer();
  }, [pathname]);

  return (
    <RightDrawerContext.Provider
      value={{
        isOpen,
        content,
        title,
        openDrawer,
        isFixed,
        closeDrawer,
        setDrawerContent,
        setDrawerTitle,
        drawerWidth,
        setDrawerWidth,
      }}
    >
      {children}
    </RightDrawerContext.Provider>
  );
};

export const useRightDrawer = () => {
  const context = useContext(RightDrawerContext);
  if (context === undefined) {
    throw new Error('useRightDrawer must be used within a RightDrawerProvider');
  }
  return context;
};
